import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Library,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowLeft,
  Settings,
  Lock,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useChaptersBySeriesSlug } from "../../hooks/useChapters";
import { useSubscriptionCheck } from "../../hooks/useSubscriptionCheck";
import styles from "./BookPreview.module.css";
import { Helmet } from "react-helmet";

// Mobile detection hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isMobile;
};

// Add helper functions for pagination
const countWords = (text) => text.trim().split(/\s+/).filter(word => word.length > 0).length;
const splitParagraphIntoSentences = (text) => {
  return text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [text];
};

// Estimate a good words-per-page based on font size and device
const computeWordsPerPage = (fontSize, isMobile) => {
  const baseAt18 = isMobile ? 170 : 300; // current defaults
  const scale = 18 / Math.max(12, Math.min(28, fontSize || 18));
  // Clamp within reasonable bounds
  const estimated = Math.round(baseAt18 * scale);
  return Math.max(90, Math.min(500, estimated));
};

// Split plain text into chunks by max words
const splitTextIntoWordChunks = (text, maxWords) => {
  const words = (text || '').trim().split(/\s+/).filter(Boolean);
  const chunks = [];
  for (let i = 0; i < words.length; i += maxWords) {
    chunks.push(words.slice(i, i + maxWords).join(' '));
  }
  return chunks;
};

function splitContentIntoPages(htmlContent, wordsPerPage = 250) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
  const container = doc.querySelector('div');
  const pages = [];
  let currentPage = document.createElement('div');
  let currentWordCount = 0;

  const commitPageIfNeeded = () => {
    if (currentPage.innerHTML.trim()) {
      pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount });
      currentPage = document.createElement('div');
      currentWordCount = 0;
    }
  };

  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!text.trim()) return;
      const words = text.trim().split(/\s+/).filter(Boolean);
      let idx = 0;
      while (idx < words.length) {
        const remaining = wordsPerPage - currentWordCount;
        if (remaining <= 0) {
          commitPageIfNeeded();
          continue;
        }
        const take = Math.min(remaining, words.length - idx);
        const chunk = words.slice(idx, idx + take).join(' ');
        currentPage.appendChild(document.createTextNode((currentWordCount > 0 ? ' ' : '') + chunk));
        currentWordCount += take;
        idx += take;
        if (currentWordCount >= wordsPerPage) {
          commitPageIfNeeded();
        }
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName;
      // For elements that contain primarily text (paragraphs, headings, list items, divs, blockquotes)
      if (['P','H1','H2','H3','H4','H5','H6','LI','DIV','BLOCKQUOTE'].includes(tag)) {
        const text = node.textContent || '';
        const totalWords = countWords(text);
        if (totalWords === 0) {
          // Append empty/structure node without affecting count
          currentPage.appendChild(node.cloneNode(true));
          return;
        }
        if (currentWordCount + totalWords <= wordsPerPage) {
          currentPage.appendChild(node.cloneNode(true));
          currentWordCount += totalWords;
        } else {
          // Split this block into chunks of words
          let remainingWordsInPage = wordsPerPage - currentWordCount;
          const words = (text || '').trim().split(/\s+/).filter(Boolean);
          let start = 0;
          while (start < words.length) {
            if (remainingWordsInPage <= 0) {
              commitPageIfNeeded();
              remainingWordsInPage = wordsPerPage;
            }
            const take = Math.min(remainingWordsInPage, words.length - start);
            const chunk = words.slice(start, start + take).join(' ');
            const newEl = document.createElement(tag);
            newEl.textContent = chunk;
            currentPage.appendChild(newEl);
            currentWordCount += take;
            start += take;
            remainingWordsInPage = wordsPerPage - currentWordCount;
            if (currentWordCount >= wordsPerPage) {
              commitPageIfNeeded();
            }
          }
        }
      } else if (['UL','OL','TABLE','IMG','PRE','CODE','HR'].includes(tag)) {
        // Non-text or special blocks: place as-is, do not count towards words
        if (currentWordCount >= wordsPerPage) {
          commitPageIfNeeded();
        }
        currentPage.appendChild(node.cloneNode(true));
      } else {
        // Other elements: clone without counting words
        currentPage.appendChild(node.cloneNode(true));
      }
    }
  });

  commitPageIfNeeded();
  return pages;
}

function BookPreview() {
  const [showLibrary, setShowLibrary] = useState(false);
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [animating, setAnimating] = useState(false);
  const [pageColor, setPageColor] = useState("#f8f5e9"); // Default cream color
  const [textColor, setTextColor] = useState("#333333"); // Default dark gray
  const [showSettings, setShowSettings] = useState(false);
  const [showPageJump, setShowPageJump] = useState(false);
  const [jumpPageNumber, setJumpPageNumber] = useState("");
  const [currentTexture, setCurrentTexture] = useState(false);
  // When switching chapters, start from this page (default 0)
  const [nextChapterInitialPage, setNextChapterInitialPage] = useState(0);

  const isMobile = useIsMobile();
  const [fontSize, setFontSize] = useState(isMobile ? 14 : 18);

  const contentRef = useRef(null);

  // Get route parameters
  const { id, slug } = useParams();
  const seriesId = id;
  const navigate = useNavigate();

  // Get series data from hook
  const { series, chapters } = useChaptersBySeriesSlug(seriesId);
  const { checkSubscriptionBeforePlay } = useSubscriptionCheck();

  // (subscription check effect moved below after currentChapter is defined)

  // Colors for theme options
  const colorThemes = [
    { bg: "#f8f5e9", text: "#333333", name: "Cream" },
    { bg: "#ffffff", text: "#333333", name: "White" },
    { bg: "#f0f0f0", text: "#333333", name: "Light Gray" },
    { bg: "#1a1a1a", text: "#e0e0e0", name: "Dark" },
    { bg: "#2d2d2d", text: "#d4c7a5", name: "Sepia Dark" },
    { bg: "#F3F3F3", text: "#2c2c2c", name: "Textured Paper", texture: true },
  ];

  // Update fontSize when device changes
  useEffect(() => {
    setFontSize(isMobile ? 14 : 18);
  }, [isMobile]);

  // Helper function to format title for URL
  const formatTitleForUrl = useCallback((title) => {
    if (!title) return "";
    const containsNonASCII = Array.from(title).some((ch) => ch.charCodeAt(0) > 127);
    if (containsNonASCII) {
      // Preserve non-ASCII characters but ensure spaces become hyphens
      return title.trim().replace(/\s+/g, "-");
    }
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  }, []);

  // Define navigation functions
  const navigateBack = useCallback(() => {
    navigate(`/book-details/${chapters[0].ebook_slug}`);
  }, [navigate, chapters]);

  // Find the current chapter index based on the URL parameter
  useEffect(() => {
    if (chapters && chapters.length > 0 && slug) {
      const slugIndex = chapters.findIndex(
        (chapter) => chapter.ebook_slug === slug
      );

      let foundIndex = -1;
      if (slugIndex !== -1) {
        foundIndex = slugIndex;
      } else {
        const titleIndex = chapters.findIndex((chapter) => {
          const formattedTitle = formatTitleForUrl(chapter.ebook_title);
          return formattedTitle === slug;
        });

        if (titleIndex !== -1) {
          foundIndex = titleIndex;
        } else {
          foundIndex = 0;
        }
      }

      if (foundIndex !== currentChapterIndex) {
        setCurrentChapterIndex(foundIndex);
      }
    }
  }, [chapters, slug, formatTitleForUrl, currentChapterIndex]);

  // Make sure we have a valid currentChapterIndex
  const validIndex = Math.min(
    Math.max(0, currentChapterIndex),
    chapters.length - 1
  );
  const currentChapter = chapters[validIndex];

  // Compute pages for ALL chapters to enable global continuous page numbering
  const { chapterPages, chapterPageStarts, globalTotalPages } = useMemo(() => {
    const result = { chapterPages: [], chapterPageStarts: [], globalTotalPages: 0 };
    if (!chapters || chapters.length === 0) return result;
    const wpp = computeWordsPerPage(fontSize, isMobile);
    let cumulative = 0;
    for (let i = 0; i < chapters.length; i++) {
      const ch = chapters[i];
      const pagesForChapter = ch?.ebook_content
        ? splitContentIntoPages(ch.ebook_content, wpp)
        : [];
      result.chapterPages.push(pagesForChapter);
      result.chapterPageStarts.push(cumulative);
      cumulative += pagesForChapter.length;
    }
    result.globalTotalPages = cumulative || 1;
    return result;
    // eslint-disable-next-line
  }, [chapters, isMobile, fontSize]);

  // Current chapter's pages derived from chapterPages
  const pages = useMemo(() => {
    return chapterPages[currentChapterIndex] || [];
  }, [chapterPages, currentChapterIndex]);

  // Keep totalPages as the global page count
  useEffect(() => {
    setTotalPages(globalTotalPages);
  }, [globalTotalPages]);

  // When chapter changes, go to the intended initial page
  useEffect(() => {
    setCurrentPage(Math.max(0, Math.min(nextChapterInitialPage, (chapterPages[currentChapterIndex]?.length || 1) - 1)));
    // reset for subsequent navigations
    setNextChapterInitialPage(0);
    // eslint-disable-next-line
  }, [currentChapterIndex]);

  // (nextPage/prevPage moved below after nextChapter/prevChapter are defined)

  const nextChapter = useCallback(() => {
    if (!chapters || currentChapterIndex >= chapters.length - 1 || animating)
      return;

    const nextIndex = currentChapterIndex + 1;
    const nextChapter = chapters[nextIndex];
    
    // Check subscription before allowing access to paid chapters
    if (nextChapter?.ebook_paid && !checkSubscriptionBeforePlay(true)) {
      return;
    }

    setAnimating(true);
    const nextChapterSlug = chapters[nextIndex].ebook_slug;

    // Next chapter should start from first page
    setNextChapterInitialPage(0);
    navigate(`/book/${id}/episode/${encodeURIComponent(nextChapterSlug)}`, {
      state: { seriesId },
    });

    setCurrentChapterIndex(nextIndex);

    setTimeout(() => {
      setAnimating(false);
    }, 100);
  }, [
    chapters,
    currentChapterIndex,
    animating,
    navigate,
    id,
    seriesId,
    checkSubscriptionBeforePlay,
    chapterPages,
  ]);

  const prevChapter = useCallback(() => {
    if (!chapters || currentChapterIndex <= 0 || animating) return;

    const prevIndex = currentChapterIndex - 1;
    const prevChapter = chapters[prevIndex];
    
    // Check subscription before allowing access to paid chapters
    if (prevChapter?.ebook_paid && !checkSubscriptionBeforePlay(true)) {
      return;
    }

    setAnimating(true);
    const prevChapterSlug = chapters[prevIndex].ebook_slug;

    // Previous chapter should open on its last page
    const prevChapterLastPage = Math.max(0, (chapterPages[prevIndex]?.length || 1) - 1);
    setNextChapterInitialPage(prevChapterLastPage);
    navigate(`/book/${id}/episode/${encodeURIComponent(prevChapterSlug)}`, {
      state: { seriesId },
    });

    setCurrentChapterIndex(prevIndex);

    setTimeout(() => {
      setAnimating(false);
    }, 100);
  }, [
    chapters,
    currentChapterIndex,
    animating,
    navigate,
    id,
    seriesId,
    checkSubscriptionBeforePlay,
  ]);

  // Next page handler (defined after nextChapter)
  const nextPage = useCallback(() => {
    const chapterPageCount = pages.length || 0;
    if (currentPage < chapterPageCount - 1) {
      setCurrentPage((prev) => prev + 1);
    } else if (currentChapterIndex < chapters.length - 1) {
      // Go to next chapter
      nextChapter();
    }
  }, [currentPage, pages.length, currentChapterIndex, chapters, nextChapter]);

  // Previous page handler (defined after prevChapter)
  const prevPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    } else if (currentChapterIndex > 0) {
      // Go to previous chapter
      prevChapter();
    }
  }, [currentPage, currentChapterIndex, prevChapter]);

  // Note: no automatic subscription redirect on mount to avoid back-button loops.

  const selectChapter = useCallback(
    (index) => {
      if (!chapters || index === currentChapterIndex || animating) return;

      // Check subscription before allowing access to paid chapters
      const selectedChapter = chapters[index];
      if (selectedChapter?.ebook_paid && !checkSubscriptionBeforePlay(true)) {
        return;
      }

      setAnimating(true);
      const selectedChapterSlug = chapters[index].ebook_slug;

      navigate(`/book/${id}/episode/${encodeURIComponent(selectedChapterSlug)}`, {
        state: { seriesId },
      });

      setCurrentChapterIndex(index);

      if (window.innerWidth < 768) {
        setShowLibrary(false);
      }

      setTimeout(() => {
        setAnimating(false);
      }, 100);
    },
    [
      chapters,
      currentChapterIndex,
      animating,
      navigate,
      id,
      seriesId,
      checkSubscriptionBeforePlay,
    ]
  );

  // Toggle library sidebar
  const toggleLibrary = useCallback(() => {
    setShowLibrary((prev) => !prev);
  }, []);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
  };

  const applyColorTheme = (bg, text, texture = false) => {
    setPageColor(bg);
    setTextColor(text);
    setCurrentTexture(texture);
  };

  const changeFontSize = (delta) => {
    const newSize = fontSize + delta;
    if (newSize >= 14 && newSize <= 24) {
      setFontSize(newSize);
    }
  };

  const handlePageJump = () => {
    const pageNum = parseInt(jumpPageNumber);
    if (pageNum >= 1 && pageNum <= totalPages) {
      const targetGlobalIndex = pageNum - 1;
      // Find chapter by global index
      let targetChapterIndex = 0;
      for (let i = 0; i < chapterPageStarts.length; i++) {
        const start = chapterPageStarts[i];
        const end = start + (chapterPages[i]?.length || 0);
        if (targetGlobalIndex >= start && targetGlobalIndex < end) {
          targetChapterIndex = i;
          break;
        }
      }

      const localPage = targetGlobalIndex - (chapterPageStarts[targetChapterIndex] || 0);

      if (targetChapterIndex !== currentChapterIndex) {
        // Navigate to the selected chapter's route
        const selectedChapterSlug = chapters[targetChapterIndex]?.ebook_slug;
        if (selectedChapterSlug) {
          navigate(`/book/${id}/episode/${encodeURIComponent(selectedChapterSlug)}`, {
            state: { seriesId },
          });
        }
        setCurrentChapterIndex(targetChapterIndex);
      }

      setCurrentPage(Math.max(0, localPage));
      setJumpPageNumber("");
      setShowPageJump(false);
    }
  };

  const handlePageJumpInputChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setJumpPageNumber(value);
    }
  };

  // Calculate pages when content changes
  useEffect(() => {
    const timer = setTimeout(() => {
      // This useEffect is no longer needed as pages are memoized
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [currentChapterIndex, fontSize]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        nextPage();
      } else if (e.key === "ArrowLeft") {
        prevPage();
      } else if (e.key === "Escape") {
        setShowSettings(false);
        setShowPageJump(false);
        setShowLibrary(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [nextPage, prevPage]);

  // Loading state
  if (!chapters || chapters.length === 0 || !series) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading book content...</p>
      </div>
    );
  }

  if (!currentChapter) {
    return (
      <div className={styles.errorContainer}>
        <h2>Chapter not found</h2>
        <button onClick={navigateBack} className={styles.backButton}>
          <ArrowLeft size={20} /> Go Back
        </button>
      </div>
    );
  }

  return (
    <>
    <Helmet>
      <title>{`${series.title} - Chapter ${currentChapter.chapter_number}: ${currentChapter.ebook_title}`}</title>
      <meta
        name="description"
        content={`Read ${currentChapter.ebook_title} from ${series.title} on SunoKahaniyan`}
      />
    </Helmet>
    <div
      className={styles.readerContainer}
      style={{ 
        backgroundColor: pageColor,
        ...(currentTexture && {
          backgroundImage: `url('/texture/graytexture.webp')`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
          backgroundPosition: '0 0'
        })
      }}
    >
      {/* Library sidebar */}
      <div
        className={`${styles.librarySidebar} ${
          showLibrary ? styles.active : ""
        }`}
        style={{ 
          backgroundColor: pageColor, 
          color: textColor,
          ...(currentTexture && {
            backgroundImage: `url('/texture/graytexture.webp')`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            backgroundAttachment: 'local'
          })
        }}
      >
        <div className={styles.libraryHeader}>
          <h2 style={{ color: textColor }}>Library</h2>
          <button
            className={styles.closeButton}
            onClick={toggleLibrary}
            style={{ color: textColor }}
          >
            <X size={24} />
          </button>
        </div>
        <div className={styles.chapterList}>
          <h3 style={{ color: textColor }}>{series.title}</h3>
          <p
            className={styles.author}
            style={{ color: textColor, opacity: 0.7 }}
          >
            by {currentChapter.ebook_author}
          </p>
          {chapters.map((chapter, index) => (
            <div
              key={chapter.id}
              className={`${styles.chapterItem} ${
                index === validIndex ? styles.active : ""
              }`}
              onClick={() => selectChapter(index)}
              style={{
                color: index === validIndex ? pageColor : textColor,
                backgroundColor:
                  index === validIndex ? textColor : "transparent",
              }}
            >
              <div className={styles.chapterHeader}>
                <span className={styles.chapterNumber}>
                  Chapter {chapter.chapter_number}
                </span>
                {chapter.ebook_paid && (
                  <Lock size={16} style={{ color: index === validIndex ? pageColor : textColor }} />
                )}
              </div>
              <span className={styles.chapterTitle}>{chapter.ebook_title}</span>
              {chapter.ebook_paid && (
                <div className={styles.paidBadge}>Premium</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Header */}
      <div
        className={styles.header}
        style={{ 
          backgroundColor: pageColor, 
          color: textColor,
          ...(currentTexture && {
            backgroundImage: `url('/texture/graytexture.webp')`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            backgroundAttachment: 'local'
          })
        }}
      >
        <button
          onClick={toggleLibrary}
          className={styles.libraryButton}
          style={{ color: textColor }}
        >
          <Library size={20} />
        </button>
        <button
          onClick={() => navigate(`/book-details/${currentChapter.ebook_slug}`)}
          className={styles.libraryButton}
          style={{ color: textColor }}
        >
          <ArrowLeft size={20} />
        </button>
        <div className={styles.bookInfo} style={{ color: textColor }}>
          <h1 style={{ color: textColor }}>
            {series.title}
            {currentChapter?.ebook_paid && (
              <span className={styles.paidIndicator}>Premium</span>
            )}
          </h1>
          <p style={{ color: textColor, opacity: 0.7 }}>
            Chapter {currentChapter.chapter_number}:{" "}
            {currentChapter.ebook_title}
          </p>
        </div>
        <button
          onClick={toggleSettings}
          className={styles.settingsButton}
          style={{ color: textColor }}
        >
          <Settings size={20} />
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div
          className={styles.settingsPanel}
          style={{ 
            backgroundColor: pageColor, 
            color: textColor,
            ...(currentTexture && {
              backgroundImage: `url('/texture/graytexture.webp')`,
              backgroundSize: '200px 200px',
              backgroundRepeat: 'repeat',
              backgroundPosition: '0 0',
              backgroundAttachment: 'local'
            })
          }}
        >
          {/* Close Button */}
          <button
            className={styles.closeButton}
            onClick={() => setShowSettings(false)}
            style={{ color: textColor }}
          >
            ×
          </button>

          <h2 className={styles.settingsTitle} style={{ color: textColor }}>
            Themes & Settings
          </h2>

          {/* Font Size Section */}
          <div className={styles.settingsSection}>
            <div className={styles.fontSizeControls}>
              <button
                onClick={() => changeFontSize(-1)}
                disabled={fontSize <= 14}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
              <div
                className={styles.fontSizeDivider}
                style={{ backgroundColor: textColor }}
              ></div>
              <button
                onClick={() => changeFontSize(1)}
                disabled={fontSize >= 24}
                className={styles.fontSizeButton}
                style={{
                  borderColor: textColor,
                  color: textColor,
                  backgroundColor: "transparent",
                }}
              >
                A
              </button>
            </div>
          </div>

          {/* Theme Options */}
          <div className={styles.settingsSection}>
            <div className={styles.themeGrid}>
              {colorThemes.map((theme, index) => (
                <button
                  key={index}
                  className={`${styles.themeCard} ${
                    theme.bg === pageColor && theme.text === textColor
                      ? styles.activeTheme
                      : ""
                  }`}
                  style={{
                    backgroundColor: theme.bg,
                    border:
                      theme.bg === pageColor && theme.text === textColor
                        ? `2px solid ${textColor}`
                        : `1px solid ${theme.text}20`,
                  }}
                  onClick={() => applyColorTheme(theme.bg, theme.text, theme.texture)}
                >
                  <div className={styles.themePreview}>
                    <span
                      className={styles.themeText}
                      style={{ color: theme.text }}
                    >
                      Aa
                    </span>
                  </div>
                  <span
                    className={styles.themeName}
                    style={{ color: theme.text }}
                  >
                    {theme.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Jump Modal */}
      {showPageJump && (
        <div className={styles.pageJumpOverlay}>
          <div
            className={styles.pageJumpModal}
            style={{ 
              backgroundColor: pageColor, 
              color: textColor,
              ...(currentTexture && {
                backgroundImage: `url('/texture/graytexture.webp')`,
                backgroundSize: '200px 200px',
                backgroundRepeat: 'repeat',
                backgroundPosition: '0 0',
                backgroundAttachment: 'local'
              })
            }}
          >
            <h3 style={{ color: textColor }}>Go to Page</h3>
            <div className={styles.pageJumpControls}>
              <input
                type="text"
                value={jumpPageNumber}
                onChange={handlePageJumpInputChange}
                placeholder={`1-${totalPages}`}
                className={styles.pageJumpInput}
                style={{
                  backgroundColor: "transparent",
                  color: textColor,
                  borderColor: textColor,
                }}
                onKeyPress={(e) => e.key === "Enter" && handlePageJump()}
                autoFocus
              />
              <button
                onClick={handlePageJump}
                className={styles.pageJumpButton}
                style={{
                  backgroundColor: textColor,
                  color: pageColor,
                }}
                disabled={
                  !jumpPageNumber ||
                  parseInt(jumpPageNumber) < 1 ||
                  parseInt(jumpPageNumber) > totalPages
                }
              >
                Go
              </button>
            </div>
            <button
              onClick={() => setShowPageJump(false)}
              className={styles.pageJumpCancel}
              style={{ color: textColor }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Content Area */}
      {/* Content Area */}
      <div
        className={styles.contentArea}
        ref={contentRef}
        style={{
          fontSize: `${fontSize}px`,
          color: textColor,
          backgroundColor: pageColor,
          ...(currentTexture && {
            backgroundImage: `url('/texture/graytexture.webp')`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            backgroundAttachment: 'local'
          })
        }}
      >
        {pages.length > 0 ? (
          <div className={styles.pageContent}>
            {currentPage === 0 && (
              <div className={styles.bookHeader}>
                <h1 className={styles.bookTitle} style={{ color: textColor }}>
                  Chapter {currentChapter.chapter_number}
                </h1>
                <h2
                  className={styles.bookSubtitle}
                  style={{ color: textColor, opacity: 0.8 }}
                >
                  {currentChapter.ebook_title}
                </h2>
                <div
                  className={styles.titleSeparator}
                  style={{ backgroundColor: textColor, opacity: 0.3 }}
                ></div>
              </div>
            )}
            {/* Add CSS for proper table and HTML element rendering */}
            <style jsx>{`
              .pageContent table {
                border-collapse: collapse;
                width: 100%;
                margin: 1rem 0;
                border: 1px solid ${textColor}40;
              }
              .pageContent td, .pageContent th {
                border: 1px solid ${textColor}40;
                padding: 8px 12px;
                text-align: left;
                vertical-align: top;
              }
              .pageContent th {
                background-color: ${textColor}10;
                font-weight: bold;
              }
              .pageContent tr:nth-child(even) {
                background-color: ${textColor}08;
              }
              .pageContent p {
                margin: 0.5rem 0;
                line-height: 1.6;
              }
              .pageContent h1, .pageContent h2, .pageContent h3, .pageContent h4, .pageContent h5, .pageContent h6 {
                margin: 1rem 0 0.5rem 0;
                font-weight: bold;
              }
              .pageContent ul, .pageContent ol {
                margin: 0.5rem 0;
                padding-left: 2rem;
              }
              .pageContent li {
                margin: 0.25rem 0;
              }
              .pageContent em {
                font-style: italic;
              }
              .pageContent strong {
                font-weight: bold;
              }
              .pageContent blockquote {
                margin: 1rem 0;
                padding: 0.5rem 1rem;
                border-left: 3px solid ${textColor}40;
                background-color: ${textColor}08;
              }
              .pageContent code {
                background-color: ${textColor}15;
                padding: 2px 4px;
                border-radius: 3px;
                font-family: monospace;
              }
              .pageContent pre {
                background-color: ${textColor}15;
                padding: 1rem;
                border-radius: 5px;
                overflow-x: auto;
                margin: 1rem 0;
              }
              .pageContent a {
                color: ${textColor};
                text-decoration: underline;
              }
              .pageContent img {
                max-width: 100%;
                height: auto;
                margin: 1rem 0;
              }
            `}</style>
            {/* Use the properly structured HTML content */}
            <div
              className="pageContent"
              dangerouslySetInnerHTML={{
                __html: pages[currentPage]?.htmlContent || "",
              }}
            />
          </div>
        ) : (
          <div
            className={styles.pageContent}
            dangerouslySetInnerHTML={{ __html: currentChapter.ebook_content }}
          />
        )}
      </div>

      {/* Pagination Controls */}
      <div
        className={styles.paginationControls}
        style={{ 
          backgroundColor: pageColor, 
          borderColor: `${textColor}20`,
          ...(currentTexture && {
            backgroundImage: `url('/texture/graytexture.webp')`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat',
            backgroundPosition: '0 0',
            backgroundAttachment: 'local'
          })
        }}
      >
        <button
          onClick={prevPage}
          disabled={(chapterPageStarts[validIndex] + currentPage) === 0}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Previous page"
        >
          <ChevronLeft size={24} />
        </button>
        <div className={styles.pageInfo} style={{ color: textColor }}>
          <button
            onClick={() => setShowPageJump(true)}
            className={styles.pageInfoButton}
            style={{ color: textColor }}
          >
            Page {chapterPageStarts[validIndex] + currentPage + 1} of {totalPages}
          </button>
        </div>
        <button
          onClick={nextPage}
          disabled={(chapterPageStarts[validIndex] + currentPage) >= totalPages - 1}
          className={styles.pageButton}
          style={{ color: textColor }}
          aria-label="Next page"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
    </>
  );
}

export default BookPreview;
